// SAB-591: commissioner entry-fee status. In paid NFL leagues the
// commissioner's seat (team + membership) exists from creation, but their
// entry fee is collected separately — they run the same paid-join money leg
// (nfl-join-league with their own invite code) from the lobby banner. Until
// they pay, the server refuses draft start with 403 COMMISSIONER_UNPAID.
//
// "Paid" is detected from the viewer's OWN wallet_transactions row
// (type='league_fee' for this league): RLS lets users read their own
// transactions, and the join RPC writes the row the moment the fee is charged.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNFLLeagueOptional } from '@/contexts/NFLLeagueContext';

export interface CommissionerFeeStatus {
  /** Viewer is this league's commissioner AND the league has a buy-in. */
  required: boolean;
  /** A league_fee wallet transaction exists for this user + league. */
  paid: boolean;
  /** True while the paid check is in flight (only ever true when required). */
  loading: boolean;
  /** The league's entry fee in dollars (0 when free or unknown). */
  entryFee: number;
  /** Re-run the paid check (call after a successful pay). */
  refresh: () => void;
}

export function useCommissionerFeeStatus(leagueId: string | null | undefined): CommissionerFeeStatus {
  const { user } = useAuth();
  const ctx = useNFLLeagueOptional();
  const league = ctx?.leagues.find(l => l.id === leagueId) ?? null;
  const entryFee = Number(league?.entry_fee ?? 0);
  const required = !!user && !!league && league.commissioner_id === user.id && entryFee > 0;

  // null = unknown (not yet checked) so surfaces never flash "unpaid" at a
  // commissioner who already paid.
  const [paidState, setPaidState] = useState<boolean | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!required || !user || !leagueId) return;
    let cancelled = false;
    setPaidState(null);
    (async () => {
      const { data } = await (supabase as any)
        .from('wallet_transactions')
        .select('id')
        .eq('user_id', user.id)
        .eq('league_id', leagueId)
        .eq('type', 'league_fee')
        .limit(1);
      if (!cancelled) setPaidState(!!data?.length);
    })();
    return () => { cancelled = true; };
  }, [required, user, leagueId, version]);

  const refresh = useCallback(() => setVersion(v => v + 1), []);

  return {
    required,
    paid: paidState === true,
    loading: required && paidState === null,
    entryFee,
    refresh,
  };
}
